import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { toast } from '@components';
import { useGetItemTypes } from '@api/RateCards/itemTypeApis';
import { ItemTypes, XCustomerID } from '@state';

/**
 * Loads a Customer's Item types into the `ItemTypes` atom, and again whenever the Customer changes:
 * the Customer named, else the one the page is for.
 */
export const useItemTypes = (namedCustomerID?: string | null) => {
	const pageCustomerID = useRecoilValue(XCustomerID);
	const customerID = namedCustomerID || pageCustomerID;
	const [itemTypes, setItemTypes] = useRecoilState(ItemTypes);
	/* Without this the reader cannot tell an outage from a Customer with no Item types: the table
	   would say "No item types yet" with nothing said about the failure. */
	const [loadFailed, setLoadFailed] = useState(false);
	/* The Customer whose list the atom last took. Until it is the current one, a Lane can't tell an
	   Item type missing from the list from a list that hasn't arrived. */
	const [loadedFor, setLoadedFor] = useState<string | null>(null);
	/* Only the latest request may write. A reload, a change of Customer and an unmount each bump
	   this, so an answer to anything older is dropped. */
	const latestRequest = useRef(0);

	const { loadingItemTypes, callGetItemTypes } = useGetItemTypes(customerID);

	const getItemTypes = () => {
		latestRequest.current += 1;
		const request = latestRequest.current;
		setLoadFailed(false);
		callGetItemTypes()
			.then((res) => {
				if (request !== latestRequest.current) return;
				if (res?.data) {
					setItemTypes(res.data.data ?? []);
					setLoadedFor(customerID);
					return;
				}
				// An unreachable server resolves with nothing rather than throwing.
				setLoadFailed(true);
			})
			.catch((err) => {
				if (request !== latestRequest.current) return;
				setLoadFailed(true);
				toast.error(err?.cause?.response?.data?.message || 'Unable to load the item types.');
			});
	}

	useEffect(() => {
		setItemTypes([]);
		setLoadedFor(null);
		// Without a Customer the API has no one's Item types to list.
		if (customerID) getItemTypes();
		return () => {
			latestRequest.current += 1;
		};
	}, [customerID]);

	return {
		itemTypes,
		loading: loadingItemTypes,
		loadFailed,
		/** Whether `itemTypes` is the current Customer's list. */
		loaded: !!customerID && loadedFor === customerID,
		getItemTypes,
	};
}
